import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Camera, ArrowRight } from "lucide-react";

const categories = [
  {
    label: "Factory Exterior",
    photos: [
      "Equipment assembly at our partner facility in Henan, China",
      "Production facility exterior — Yucheng Industrial Zone",
    ],
  },
  {
    label: "Workshop Interior",
    photos: [
      "Welding and assembly workshop",
      "Precision machining center",
    ],
  },
  {
    label: "Equipment & Quality",
    photos: [
      "Pre-shipment quality inspection",
      "Completed pyrolysis plant ready for delivery",
      "Distillation unit during final testing",
    ],
  },
  {
    label: "Shipping",
    photos: [
      "Container loading for overseas delivery",
      "Heavy equipment transport preparation",
    ],
  },
];

export default function Factory() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  let cardIndex = 0;

  return (
    <>
      {/* Hero */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight"
          >
            Our Factory Network
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-5 max-w-2xl mx-auto text-lg text-slate-300"
          >
            Direct access to 10+ certified production facilities in Yucheng,
            Henan — China&rsquo;s most concentrated thermal conversion equipment
            manufacturing cluster.
          </motion.p>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {categories.map((category) => (
          <div key={category.label} className="mb-14 last:mb-0">
            <h2 className="text-xl font-semibold text-slate-800 mb-6 border-b border-slate-200 pb-3">
              {category.label}
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.photos.map((caption) => {
                const i = cardIndex++;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: (i % 3) * 0.1 }}
                  >
                    <div className="bg-slate-100 aspect-[4/3] rounded-xl flex flex-col items-center justify-center gap-2">
                      <Camera className="w-10 h-10 text-slate-300" />
                      <span className="text-slate-400 text-sm">
                        Photo coming soon
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-slate-600">{caption}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Bottom CTA */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Want to visit our factories?
            </h2>
            <p className="mt-4 max-w-xl mx-auto text-slate-600">
              We arrange factory tours and video calls for serious buyers.
              Contact us to schedule.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#00bf63] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#00a855] transition-colors"
            >
              Schedule a Visit
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
